import { MutableRefObject } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { FigureModel } from '../../../components/views/StoreBuilderDetailsView/figures';
import { StoreBuildModel } from '../../../models/store-build';

export interface FebViewProps {
  build?: StoreBuildModel;
  sidebarCollapsed?: boolean;
  onSaveStatusChange?: (status: any) => void;
}

export interface FixtureItem {
  id: string;
  name: string;
  color: string;
  size: { width: number; height: number };
  imageUrl?: string;
  category?: string;
  figure?: FigureModel & { [key: string]: any };
}

export enum QueueLineColors {
  TealBlue = '#003D4C',
  Blue = '#2D9CDB',
  Pink = '#DA1884',
  Orange = '#F2994A',
  Green = '#27AE60',
}

export const queueLineColorMap = {
  1: QueueLineColors.TealBlue,
  2: QueueLineColors.Blue,
  3: QueueLineColors.Pink,
  4: QueueLineColors.Orange,
  5: QueueLineColors.Green,
};

export enum ShopperType {
  WomanWithBasket = 1,
  WomanWithCart = 2,
  WomanWithoutBasket = 3,
  SmallManWithBasket = 4,
  SmallManWithoutBasket = 5,
  LargeManWithBasket = 6,
  LargeManWithoutBasket = 7,
}

export interface ShopperMesh extends THREE.Mesh {
  userData: {
    isInteractive: boolean;
    isQueueLineShopper: boolean;
    orderInLine: number;
    lineId: number;
    color: QueueLineColors;
    type: ShopperType;
    elapsedWaitTime: string;
    originalMeshId?: string;
    badgeContainer?: THREE.Object3D;
    hasOverlay?: string;
    collisionBox?: THREE.Box3;
  };
}

export interface QueueLineModel {
  lineId: number;
  color: QueueLineColors;
  shoppers: ShopperMesh[];
}

export interface ThreeRefs {
  scene: MutableRefObject<THREE.Scene | null>;
  camera: MutableRefObject<THREE.Camera | null>;
  renderer: MutableRefObject<THREE.WebGLRenderer | null>;
  controls: MutableRefObject<OrbitControls | null>;
  raycaster: MutableRefObject<THREE.Raycaster>;
  floorPlane: MutableRefObject<THREE.Mesh | null>;
  selectedMesh: MutableRefObject<THREE.Mesh | null>;
  selectedMeshes: MutableRefObject<THREE.Mesh[]>;
  isDraggingMesh: MutableRefObject<boolean>;
  dragPlane: MutableRefObject<THREE.Plane>;
  intersectionPoint: MutableRefObject<THREE.Vector3>;
  meshOffset: MutableRefObject<THREE.Vector3>;
  gridHelper: MutableRefObject<THREE.GridHelper | null>;
  isInQueueLineMode: MutableRefObject<boolean>;
  currentQueueLine: MutableRefObject<QueueLineModel | null>;
  queueLines: MutableRefObject<QueueLineModel[]>;
  selectedShopperType: MutableRefObject<ShopperType>;
}

// Add a type guard function to check if a mesh is a ShopperMesh
export function isShopperMesh(mesh: THREE.Object3D): mesh is ShopperMesh {
  return (
    mesh instanceof THREE.Mesh &&
    'userData' in mesh &&
    typeof mesh.userData === 'object' &&
    mesh.userData !== null &&
    'isInteractive' in mesh.userData &&
    'isQueueLineShopper' in mesh.userData &&
    'orderInLine' in mesh.userData &&
    'lineId' in mesh.userData &&
    'color' in mesh.userData &&
    'type' in mesh.userData &&
    'elapsedWaitTime' in mesh.userData
  );
}
